import {EventProcessStageTitle, Tag, TaskRepetitionLabel} from '@hconnect/common/components'
import {mapTaskRepetitionDtoToTaskRepetition} from '@hconnect/common/mappers'
import {DateFormat, GridSetting} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Grid} from '@mui/material'
import {
  EventStatusLabel,
  EventTypeLabel,
  getEquipmentLabelDetails
} from '@widgets/common-shift-event'
import moment from 'moment-timezone'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {ShiftTaskDto} from '../../types'

import {TaskDescription} from './TaskDescription'

const formatAssignees = (assignees: ShiftTaskDto['assignees']) => {
  return assignees?.map(({name}) => name).join(', ') || '-'
}

const regularSize: GridSetting = {
  xs: 12,
  sm: 4,
  md: 6
}

type Props = {
  shiftTask: ShiftTaskDto
  plantId: string
}

export const ShiftTaskDetails = ({shiftTask, plantId}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)

  const translate = (key: string) => t(key)
  const noneOfThatLabel = translate('shiftEvent.action.noneOfThat')

  const defaultRepetitionStartDate = useMemo(
    () => (shiftTask.dueDate ? moment(shiftTask.dueDate) : undefined),
    [shiftTask.dueDate]
  )
  const taskRepetitionSettings = useMemo(
    () =>
      shiftTask.repetitionInfo
        ? mapTaskRepetitionDtoToTaskRepetition(shiftTask.repetitionInfo, timezone)
        : null,
    [shiftTask.repetitionInfo, timezone]
  )

  return (
    <>
      <Grid container spacing={2}>
        <Grid item {...regularSize}>
          <Tag
            label={translate('shiftEvent.label.eventType')}
            data-test-id="task-details-eventType"
          >
            <EventTypeLabel eventType={shiftTask.eventType} />
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={translate('task.label.status')} data-test-id="task-details-status">
            <EventStatusLabel status={shiftTask.status} />
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={translate('shiftEvent.label.category')} data-test-id="task-details-category">
            {shiftTask.category
              ? translate(`shiftEvent.category.${shiftTask.category}`)
              : noneOfThatLabel}
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag
            label={translate('shiftEvent.label.processStage')}
            data-test-id="task-details-processStage"
          >
            {shiftTask.processStage ? (
              <EventProcessStageTitle plantId={plantId} stage={shiftTask.processStage} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            data-test-id="task-details-mainEquipment"
            label={translate('shiftEvent.label.mainEquipment')}
          >
            {getEquipmentLabelDetails(shiftTask.mainEquipment, false)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            data-test-id="task-details-equipment"
            label={translate('shiftEvent.label.equipment')}
          >
            {getEquipmentLabelDetails(shiftTask.equipment)}
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={translate('task.label.dueDate')} data-test-id="task-details-dueDate">
            {timezone ? (
              <DateFormat date={shiftTask.dueDate} timezone={timezone} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={translate('shiftEvent.label.priority')} data-test-id="task-details-priority">
            {shiftTask.priority ?? noneOfThatLabel}
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={translate('task.label.assignee')} data-test-id="task-details-assignee">
            {formatAssignees(shiftTask.assignees)}
          </Tag>
        </Grid>
      </Grid>
      {taskRepetitionSettings && (
        <Grid item xs={12}>
          <Tag label={translate('shiftEvent.label.recurring')}>
            <TaskRepetitionLabel
              settings={taskRepetitionSettings}
              defaultStartDate={defaultRepetitionStartDate}
              omitFirstOccurrence
              plantId={plantId}
              timezone={timezone}
              namespace={FEATURE_NAMESPACE}
            />
          </Tag>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.description')}
            descriptionSx={{fontWeight: 400, fontSize: 14}}
            data-test-id="task-details-description"
          >
            <TaskDescription description={shiftTask.description} />
          </Tag>
        </Grid>
      </Grid>
    </>
  )
}
